import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type Language = "en" | "bn";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// English translations
const en: Record<string, string> = {
  // Common
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.search": "Search",
  "common.loading": "Loading...",
  "common.refresh": "Refresh",
  "common.export": "Export",
  "common.import": "Import",
  "common.all": "All",
  "common.today": "Today",
  "common.thisMonth": "This Month",
  "common.thisYear": "This Year",
  "common.paid": "Paid",
  "common.due": "Due",
  "common.active": "Active",
  "common.inactive": "Inactive",
  "common.yes": "Yes",
  "common.no": "No",

  // Header
  "nav.features": "Features",
  "nav.pricing": "Pricing",
  "nav.faq": "FAQ",
  "nav.contact": "Contact",
  "nav.dashboard": "Dashboard",
  "nav.settings": "Settings",
  "nav.logout": "Log out",
  "nav.login": "Login",
  "nav.startTrial": "Start Free Trial",
  "nav.theme": "Theme",
  "nav.language": "Language",

  // Hero Section
  "hero.badge": "AI-Powered Business Platform",
  "hero.title1": "Automate Your",
  "hero.title2": "Business",
  "hero.title3": "in Seconds",
  "hero.subtitle": "Complete business automation - AI-powered platform for E-commerce & F-commerce. Plus powerful POS system for offline shops. Manage everything from one platform.",
  "hero.subtitleBold": "Start free today. No credit card needed.",
  "hero.cta": "Start Free Trial",
  "hero.demo": "Watch Demo",
  "hero.benefit1": "24-hour free trial",
  "hero.benefit2": "No credit card required",
  "hero.benefit3": "Cancel anytime",
  "hero.statsMessages": "Messages",
  "hero.statsHandled": "Handled today",
  "hero.statsSuccess": "Success",
  "hero.statsAutoReply": "Auto-reply rate",

  // Features Section
  "features.badge": "Powerful Features",
  "features.title1": "Everything You Need to",
  "features.title2": "Automate Your Shop",
  "features.subtitle": "From instant message replies to intelligent image recognition — we handle it all so you can focus on growing your business.",
  "features.learnMore": "Learn more",
  "features.messageReply.title": "Message Auto-Reply",
  "features.messageReply.desc": "AI responds to customer messages in Bengali instantly. Never miss a sale again.",
  "features.imageRecognition.title": "Image Recognition",
  "features.imageRecognition.desc": "Customers send product images, AI identifies and prices them automatically.",
  "features.voiceSupport.title": "Voice Support",
  "features.voiceSupport.desc": "Voice messages? We transcribe and respond automatically in seconds.",
  "features.commentManagement.title": "Comment Management",
  "features.commentManagement.desc": "Spam detection, auto-delete bad comments, ban abusive users instantly.",
  "features.autoInvoice.title": "Auto-Invoice",
  "features.autoInvoice.desc": "Generate professional invoices automatically after every order confirmation.",
  "features.support247.title": "24/7 Support",
  "features.support247.desc": "Your shop never sleeps. Respond to customers anytime, day or night.",
  
  // Offline Shop Features
  "features.onlineBusiness": "Online Business Automation",
  "features.offlineShopBusiness": "Offline Shop Management",
  "features.offlineShop.title": "Shop Management",
  "features.offlineShop.desc": "Complete POS system with inventory, sales, and expense tracking.",
  "features.inventory.title": "Inventory Control",
  "features.inventory.desc": "Track stock levels, expiry dates, and get low stock alerts.",
  "features.customers.title": "Customer Management",
  "features.customers.desc": "Build customer relationships with due tracking and purchase history.",
  "features.reports.title": "Reports & Analytics",
  "features.reports.desc": "Make data-driven decisions with comprehensive business reports.",
  "features.expense.title": "Expense & Cash",
  "features.expense.desc": "Track expenses, manage cash flow, and daily cash closing.",
  "features.sync.title": "Online-Offline Sync",
  "features.sync.desc": "Unify inventory across online and offline sales channels.",
  "features.suppliers.title": "Supplier Management",
  "features.suppliers.desc": "Track supplier purchases, dues, and payment history.",
  "features.loans.title": "Loan Management",
  "features.loans.desc": "Track business loans with installments and payment reminders.",

  // FAQ Section
  "faq.badge": "FAQ",
  "faq.title1": "Frequently Asked",
  "faq.title2": "Questions",
  "faq.subtitle": "Got questions? We've got answers. If you can't find what you're looking for, feel free to contact our support team.",
  "faq.q1": "What is the difference between Online Business and Offline Shop?",
  "faq.a1": "Online Business is for Facebook/WhatsApp automation - auto-reply to messages, image recognition, comment management. Offline Shop is for physical stores - POS, inventory, sales tracking, invoicing. You can use both together with our sync feature!",
  "faq.q2": "Is the free trial really free?",
  "faq.a2": "Yes! Online Business trial is 24 hours, Offline Shop trial is 7 days. Full access to all features. No credit card required. If you don't upgrade, your data stays safe - you can resume anytime.",
  "faq.q3": "Will the AI respond in Bengali?",
  "faq.a3": "Absolutely! Our AI is specifically trained to understand and respond in Bengali naturally. It handles Bengali slang, Banglish, and even voice messages in Bengali perfectly.",
  "faq.q4": "Can I use Offline Shop without internet?",
  "faq.a4": "Yes! Core features work offline. Record sales, track inventory, generate invoices - all without internet. Data syncs automatically when you're connected.",
  "faq.q5": "How does Online-Offline sync work?",
  "faq.a5": "When enabled, orders from Facebook automatically reduce stock from your Offline Shop inventory. You get unified reports across all channels. One inventory, multiple sales channels!",
  "faq.q6": "Is my data secure?",
  "faq.a6": "100% secure. We use enterprise-grade encryption. We never store your Facebook password. Your business data is yours - we never sell or share it.",
  "faq.q7": "Can multiple staff use the Offline Shop?",
  "faq.a7": "Yes! Create staff accounts with specific permissions - cashiers can only record sales, managers can see reports, etc. Full access control for your team.",
  "faq.q8": "What payment methods are supported?",
  "faq.a8": "We support bKash, Nagad, and bank transfers for Bangladesh. Track all payments including Cash, bKash, Nagad, Card, and Bank in your Offline Shop.",

  // CTA Section
  "cta.title1": "Ready to",
  "cta.title2": "Automate",
  "cta.title3": "Your Shop?",
  "cta.subtitle": "Join thousands of businesses already saving time and increasing sales with AI automation.",
  "cta.button": "Start Free Trial Now",
  "cta.note": "No credit card required • Cancel anytime",

  // Benefits Section
  "benefits.badge": "Why Choose Us",
  "benefits.title1": "Built for",
  "benefits.title2": "Bangladeshi Businesses",
  "benefits.subtitle": "We understand the local market. That's why we've built features specifically designed for Facebook Commerce in Bangladesh.",

  // Footer
  "footer.product": "Product",
  "footer.company": "Company",
  "footer.resources": "Resources",
  "footer.legal": "Legal",
  "footer.features": "Features",
  "footer.pricing": "Pricing",
  "footer.documentation": "Documentation",
  "footer.about": "About",
  "footer.blog": "Blog",
  "footer.careers": "Careers",
  "footer.contact": "Contact",
  "footer.helpCenter": "Help Center",
  "footer.privacyPolicy": "Privacy Policy",
  "footer.termsOfService": "Terms of Service",
  "footer.cookiePolicy": "Cookie Policy",
  "footer.gdpr": "GDPR",

  // Dashboard
  "dashboard.title": "Dashboard",
  "dashboard.welcome": "Welcome back",
  "dashboard.overview": "Your business overview",
  "dashboard.onlineBusiness": "Online Business",
  "dashboard.offlineShop": "Offline Shop",
  "dashboard.messagesHandled": "Messages Handled",
  "dashboard.autoReplies": "Auto-Replies",
  "dashboard.todayOrders": "Today's Orders",
  "dashboard.activeAutomations": "Active Automations",
  "dashboard.todaySales": "Today's Sales",
  "dashboard.todayProfit": "Today's Profit",
  "dashboard.totalProducts": "Total Products",
  "dashboard.totalCustomers": "Total Customers",
  "dashboard.monthSales": "This Month's Sales",
  "dashboard.monthExpenses": "This Month's Expenses",
  "dashboard.monthProfit": "This Month's Profit",
  "dashboard.monthTarget": "Monthly Target",
  "dashboard.salesTarget": "Monthly Sales Target",
  "dashboard.targetAchieved": "Target Achieved",
  "dashboard.ofTarget": "of target",
  "dashboard.inventoryValue": "Stock Value",
  "dashboard.totalDue": "Total Due",
  "dashboard.lowStockAlert": "Low Stock Alert",
  "dashboard.recentSales": "Recent Sales",
  "dashboard.recentActivity": "Recent Activity",
  "dashboard.connectedPlatforms": "Connected Platforms",
  "dashboard.noPlatforms": "No platforms connected",
  "dashboard.noActivity": "No recent activity",
  "dashboard.noSales": "No sales yet",
  "dashboard.noLowStock": "No low stock products",
  "dashboard.successRate": "Success Rate",
  "dashboard.manageAutomations": "Manage Automations",
  "dashboard.newAutomation": "New Automation",
  "dashboard.goToOfflineShop": "Go to Offline Shop",
  "dashboard.goToOnline": "Go to Online Business",
  "dashboard.remaining": "remaining",
  "dashboard.returnsSummary": "Returns Summary",
  "dashboard.returnsOverview": "Product returns and refunds overview",
  "dashboard.totalReturns": "Total Returns",
  "dashboard.totalRefunds": "Total Refunds",
  "dashboard.processed": "Processed",
  "dashboard.pending": "Pending",
  "dashboard.topReturnReasons": "Top Return Reasons",
  "dashboard.returns": "returns",
  "dashboard.noReturns": "No returns recorded yet",
  "dashboard.viewAll": "View All",
  "dashboard.recentProducts": "Recently Added",
  "dashboard.recentProductsDesc": "Latest products added to inventory",
  "dashboard.noProducts": "No products added yet",

  // Offline Shop
  "shop.dashboard": "Dashboard",
  "shop.products": "Products",
  "shop.sales": "Sales",
  "shop.purchases": "Purchases",
  "shop.expenses": "Expenses",
  "shop.customers": "Customers",
  "shop.suppliers": "Suppliers",
  "shop.reports": "Reports",
  "shop.staff": "Staff",
  "shop.adjustments": "Adjustments",
  "shop.cash": "Cash",
  "shop.settings": "Settings",
  "shop.newProduct": "New Product",
  "shop.newSale": "New Sale",
  "shop.newPurchase": "New Purchase",
  "shop.newExpense": "New Expense",
  "shop.productName": "Product Name",
  "shop.sku": "SKU",
  "shop.barcode": "Barcode",
  "shop.purchasePrice": "Purchase Price",
  "shop.sellingPrice": "Selling Price",
  "shop.stock": "Stock",
  "shop.category": "Category",
  "shop.unit": "Unit",
  "shop.expiryDate": "Expiry Date",
  "shop.supplier": "Supplier",
  "shop.description": "Description",
  "shop.minStockAlert": "Minimum Stock Alert",
  "shop.walkInCustomer": "Walk-in Customer",
  "shop.invoiceNumber": "Invoice Number",
  "shop.customer": "Customer",
  "shop.total": "Total",
  "shop.subtotal": "Subtotal",
  "shop.discount": "Discount",
  "shop.tax": "Tax",
  "shop.paidAmount": "Paid Amount",
  "shop.dueAmount": "Due Amount",
  "shop.paymentMethod": "Payment Method",
  "shop.paymentStatus": "Payment Status",
  "shop.actions": "Actions",
  
  // Shop Products Page
  "shop.productsTitle": "Products",
  "shop.productsDesc": "Manage all your products",
  "shop.searchProducts": "Search products...",
  "shop.noProducts": "No products",
  "shop.editProduct": "Edit Product",
  "shop.productDetails": "Enter product details",
  "shop.pcs": "Pcs",
  "shop.kg": "KG",
  "shop.liter": "Liter",
  "shop.dozen": "Dozen",
  "shop.productAdded": "Product added",
  "shop.productUpdated": "Product updated",
  "shop.productDeleted": "Product deleted",
  "shop.deleteConfirm": "Are you sure you want to delete this product?",
  "shop.excelDownloaded": "Excel file downloaded",
  "shop.importSuccess": "products imported",
  "shop.importFailed": "products failed to import",
  "shop.loadError": "Error loading data",
  "shop.errorOccurred": "An error occurred, please try again",
  
  // Shop Sales Page
  "shop.salesTitle": "Sales",
  "shop.salesDesc": "Manage your sales",
  "shop.noSales": "No sales",
  "shop.cart": "Cart",
  "shop.cartEmpty": "Cart is empty",
  "shop.selectProduct": "Select Product",
  "shop.selectCustomer": "Select customer (optional)",
  "shop.completeSale": "Complete Sale",
  "shop.saleComplete": "Sale complete!",
  "shop.invoice": "Invoice",
  "shop.date": "Date",
  "shop.status": "Status",
  "shop.view": "View",
  "shop.print": "Print",
  "shop.close": "Close",
  "shop.cashPayment": "Cash",
  "shop.bkash": "bKash",
  "shop.nagad": "Nagad",
  "shop.card": "Card",
  "shop.noProductsInCart": "No products in cart",
  "shop.quantity": "Quantity",
  "shop.price": "Price",
  "shop.billTo": "Bill To",
  "shop.invoiceDetails": "Invoice Details",
  "shop.orderItems": "Order Items",
  "shop.unitPrice": "Unit Price",
  
  // Shop Purchases Page
  "shop.purchasesTitle": "Purchases",
  "shop.purchasesDesc": "Record of products bought from suppliers",
  "shop.noPurchases": "No purchases",
  "shop.purchaseComplete": "Purchase completed",
  "shop.addItems": "Add items",
  "shop.item": "Item",
  "shop.contact": "Contact",
  "shop.notes": "Notes",
  "shop.purchaseDetails": "Purchase Details",
  "shop.supplierName": "Supplier Name",
  "shop.buyFromSupplier": "Buy products from supplier",
  
  // Shop Expenses Page
  "shop.expensesTitle": "Expenses",
  "shop.expensesDesc": "Track all shop expenses",
  "shop.noExpenses": "No expenses",
  "shop.totalExpenses": "Total Expenses",
  "shop.expenseAdded": "Expense added",
  "shop.expenseDetails": "Enter expense details",
  "shop.amount": "Amount",
  "shop.rent": "Rent",
  "shop.electricity": "Electricity Bill",
  "shop.water": "Water Bill",
  "shop.internet": "Internet",
  "shop.salary": "Employee Salary",
  "shop.transport": "Transport",
  "shop.marketing": "Marketing",
  "shop.maintenance": "Maintenance",
  "shop.officeSupply": "Office Supply",
  "shop.other": "Other",
  "shop.select": "Select",
  "shop.bank": "Bank",
  "shop.payment": "Payment",
  
  // Shop Customers Page
  "shop.customersTitle": "Customers",
  "shop.customersDesc": "Manage your customers",
  "shop.noCustomers": "No customers",
  "shop.searchCustomers": "Search customers...",
  "shop.customerAdded": "Customer added",
  "shop.customerUpdated": "Customer updated",
  "shop.editCustomer": "Edit Customer",
  "shop.newCustomer": "New Customer",
  "shop.customerDetails": "Enter customer details",
  "shop.name": "Name",
  "shop.phone": "Phone",
  "shop.email": "Email",
  "shop.address": "Address",
  "shop.totalPurchases": "Total Purchases",
  
  // Shop Suppliers Page
  "shop.suppliersTitle": "Suppliers",
  "shop.suppliersDesc": "Manage all your suppliers",
  "shop.noSuppliers": "No suppliers",
  "shop.searchSuppliers": "Search suppliers...",
  "shop.newSupplier": "New Supplier",
  "shop.editSupplier": "Edit Supplier",
  "shop.supplierAdded": "Supplier added",
  "shop.supplierUpdated": "Supplier updated",
  "shop.supplierDeleted": "Supplier deleted",
  "shop.supplierDetails": "Enter supplier details",
  "shop.details": "Details",
  "shop.recentPurchases": "Recent Purchases",
  "shop.noPurchasesYet": "No purchases yet",
  
  // Shop Adjustments Page
  "shop.adjustmentsTitle": "Damage / Adjustments",
  "shop.adjustmentsDesc": "Record stock adjustments",
  "shop.noAdjustments": "No adjustments",
  "shop.newAdjustment": "New Adjustment",
  "shop.adjustmentSaved": "Adjustment saved",
  "shop.totalLoss": "Total Loss (Damage/Expiry/Theft)",
  "shop.type": "Type",
  "shop.costImpact": "Cost Impact",
  "shop.reason": "Reason",
  "shop.damage": "Damage",
  "shop.loss": "Loss",
  "shop.expired": "Expired",
  "shop.theft": "Theft",
  "shop.manualIncrease": "Manual Increase",
  "shop.manualDecrease": "Manual Decrease",
  "shop.return": "Return",
  "shop.allTypes": "All Types",
  "shop.stockAdjustment": "Stock Adjustment",
  "shop.adjustmentDetails": "Enter adjustment details",
  "shop.selectProductAdj": "Select product",
  
  // Shop Cash Page
  "shop.cashTitle": "Cash & Payments",
  "shop.cashDesc": "Manage your cashbook",
  "shop.noTransactions": "No transactions",
  "shop.newTransaction": "New Transaction",
  "shop.transactionSaved": "Transaction saved",
  "shop.currentBalance": "Current Balance",
  "shop.totalDeposit": "Total Deposit",
  "shop.totalWithdraw": "Total Withdraw",
  "shop.customerDue": "Customer Due",
  "shop.supplierDue": "Supplier Due",
  "shop.noDue": "No due",
  "shop.deposit": "Deposit",
  "shop.withdraw": "Withdraw",
  "shop.source": "Source",
  "shop.ownerDeposit": "Owner Deposit",
  "shop.ownerWithdraw": "Owner Withdraw",
  "shop.dueCollection": "Due Collection",
  "shop.duePayment": "Due Payment",
  "shop.adjustment": "Adjustment",
  "shop.sale": "Sale",
  "shop.purchase": "Purchase",
  "shop.expense": "Expense",
  "shop.cashIn": "Cash In",
  "shop.cashOut": "Cash Out",
  "shop.selectCustomerDue": "Select customer",
  "shop.selectSupplierDue": "Select supplier",
  "shop.transactionDetails": "Enter cash deposit or withdraw details",
  
  // Shop Staff Page
  "shop.staffTitle": "Staff & Permissions",
  "shop.staffDesc": "Manage your shop staff",
  "shop.noStaff": "No staff",
  "shop.newStaff": "New Staff",
  "shop.editStaff": "Edit Staff",
  "shop.staffAdded": "Staff added",
  "shop.staffUpdated": "Staff updated",
  "shop.staffDeleted": "Staff deleted",
  "shop.staffDetails": "Set staff details and permissions",
  "shop.role": "Role",
  "shop.permissions": "Permissions",
  "shop.owner": "Owner",
  "shop.manager": "Manager",
  "shop.cashier": "Cashier",
  "shop.staffMember": "Staff",
  "shop.canSeeReports": "Can view reports",
  "shop.canEditProducts": "Can edit products",
  "shop.canRecordPurchases": "Can record purchases",
  "shop.canRecordSales": "Can record sales",
  "shop.canSeeProfit": "Can see profit",
  "shop.canManageStaff": "Can manage staff",
  "shop.staffAccessControl": "Staff Access Control",
  "shop.staffAccessDesc": "Here you can create staff for your shop and determine what permissions they have. In the future, each staff will be able to login separately.",
  "shop.emailPhone": "Email/Phone",
  
  // Shop Reports Page
  "shop.reportsTitle": "Reports",
  "shop.reportsDesc": "View and download detailed reports",
  "shop.selectDate": "Select Date",
  "shop.start": "Start",
  "shop.end": "End",
  "shop.salesReport": "Sales Report",
  "shop.salesReportDesc": "Detailed sales for selected period",
  "shop.expensesReport": "Expenses Report",
  "shop.expensesReportDesc": "Detailed expenses for selected period",
  "shop.inventoryReport": "Inventory Report",
  "shop.inventoryReportDesc": "Current stock details",
  "shop.download": "Download",
  "shop.reportDownloaded": "Report downloaded",
  "shop.clickToViewReport": "Click above to view report",
  "shop.totalSales": "Total Sales",
  "shop.totalProfit": "Total Profit",
  "shop.totalOrders": "Total Orders",
  "shop.stockValue": "Stock Value",
  "shop.lowStock": "Low Stock",
  
  // Shop Settings Page
  "shop.settingsTitle": "Shop Settings",
  "shop.settingsDesc": "Configure your shop details",
  "shop.shopInfo": "Shop Information",
  "shop.shopInfoDesc": "This information will be shown on invoices",
  "shop.shopName": "Shop Name",
  "shop.shopNamePlaceholder": "Your shop name",
  "shop.shopAddress": "Address",
  "shop.shopAddressPlaceholder": "Shop address",
  "shop.shopPhone": "Phone",
  "shop.shopEmail": "Email",
  "shop.invoiceSettings": "Invoice Settings",
  "shop.invoiceSettingsDesc": "Customize invoices",
  "shop.invoicePrefix": "Invoice Prefix",
  "shop.taxRate": "Tax Rate (%)",
  "shop.invoiceFooter": "Invoice Footer",
  "shop.invoiceFooterPlaceholder": "Thank you! Come again.",
  "shop.termsAndConditions": "Terms & Conditions",
  "shop.termsAndConditionsPlaceholder": "Enter terms and conditions that will appear on invoices...",
  "shop.invoiceFormat": "Invoice Format",
  "shop.simpleInvoice": "Simple Invoice",
  "shop.simpleInvoiceDesc": "Clean minimal design with basic info",
  "shop.betterInvoice": "Better Invoice",
  "shop.betterInvoiceDesc": "Professional design with dark header & orange accents",
  "shop.settingsSaved": "Settings saved",
  "shop.saving": "Saving...",
  "shop.shopLogo": "Shop Logo",
  "shop.uploadLogo": "Upload Logo",
  "shop.uploading": "Uploading...",
  "shop.logoUploaded": "Logo uploaded successfully",
  "shop.logoRemoved": "Logo removed",
  "shop.logoHint": "PNG, JPG up to 2MB. This will appear on invoices.",
  "shop.invalidImageType": "Please select an image file",
  "shop.imageTooLarge": "Image must be less than 2MB",
  
  // Shop Layout
  "shop.offlineShop": "Offline Shop",
  "shop.youAreIn": "You are in:",
  "shop.offlineShopBusiness": "Offline Shop Business",
  "shop.goToOnline": "Go to Online Business",
  "shop.switchSection": "Switch Section",
  "shop.overview": "Overview",
  "shop.productsStock": "Products & Stock",
  "shop.purchasesBuyImport": "Purchases (Buy/Import)",
  "shop.salesSellPOS": "Sales (Sell/POS)",
  "shop.returns": "All Returns",
  "shop.damageAdjustments": "Damage/Adjustments",
  "shop.cashPayments": "Cash & Payments",
  "shop.staffPermissions": "Staff & Permissions",
  "shop.settingsSync": "Settings & Sync",
  "shop.update": "Update",
  
  // Additional Shop Translations
  "shop.noStock": "Not enough stock",
  "shop.selectAtLeastOne": "Select at least one product",
  "shop.returnComplete": "Return completed!",
  "shop.selectProductComplete": "Select products and complete the sale",
  "shop.customerNameOptional": "Customer Name (optional)",
  "shop.phoneOptional": "Phone Number (optional)",
  "shop.thankYou": "Thank you! Please come again.",
  "shop.productReturn": "Product Return",
  "shop.selectReturnItems": "Select return items",
  "shop.max": "Max",
  "shop.returnReason": "Return Reason",
  "shop.selectReason": "Select reason",
  "shop.defective": "Defective Product",
  "shop.wrongItem": "Wrong Product",
  "shop.notSatisfied": "Not Satisfied",
  "shop.sizeIssue": "Size Issue",
  "shop.notesOptional": "Additional Notes (optional)",
  "shop.totalRefund": "Total Refund",
  "shop.confirmReturn": "Confirm Return",
  "shop.reasonPlaceholder": "e.g. Broken, water damage...",

  // Settings
  "settings.title": "Settings",
  "settings.account": "Account",
  "settings.automation": "Automation",
  "settings.automations": "Automations",
  "settings.connections": "Connections",
  "settings.services": "Services",
  "settings.notifications": "Notifications",
  "settings.billing": "Billing",
  "settings.language": "Language",
  "settings.languageDesc": "Choose your preferred interface language",
  "settings.english": "English",
  "settings.bengali": "Bengali (বাংলা)",
  "settings.profile": "Profile Information",
  "settings.profileDesc": "Update your personal details",
  "settings.name": "Full Name",
  "settings.email": "Email Address",
  "settings.phone": "Phone Number",
  "settings.password": "Password",
  "settings.changePassword": "Change Password",
  "settings.deleteAccount": "Delete Account",
  "settings.deleteAccountDesc": "Permanently delete your account and all data",
  "settings.saveChanges": "Save Changes",
  "settings.aiPreferences": "AI Preferences",
  "settings.responseTone": "Response Tone",
  "settings.responseLanguage": "Response Language",
  "settings.friendly": "Friendly",
  "settings.professional": "Professional",
  "settings.casual": "Casual",
  "settings.emailNotifications": "Email Notifications",
  "settings.pushNotifications": "Push Notifications",
  "settings.soundAlerts": "Sound Alerts",
  "settings.dailyDigest": "Daily Digest",
  "settings.syncSettings": "Sync Settings",
  "settings.syncDesc": "Connect your Online and Offline business data",
  "settings.enableSync": "Enable Sync",
  "settings.syncEnabled": "Sync is enabled",
  "settings.syncDisabled": "Sync is disabled",
  "settings.currentPlan": "Current Plan",
  "settings.upgradePlan": "Upgrade Plan",
  "settings.manageBilling": "Manage Billing",
  "settings.dangerZone": "Danger Zone",
  "settings.dangerZoneDesc": "Irreversible actions that affect your account",
  "settings.deleteAccountConfirm": "This will permanently delete your account and all associated data.",

  // Auth
  "auth.login": "Login",
  "auth.signup": "Sign Up",
  "auth.forgotPassword": "Forgot Password?",
  "auth.resetPassword": "Reset Password",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.confirmPassword": "Confirm Password",
  "auth.rememberMe": "Remember me",
  "auth.noAccount": "Don't have an account?",
  "auth.hasAccount": "Already have an account?",
  "auth.createAccount": "Create Account",
  "auth.loginWithFacebook": "Login with Facebook",
  "auth.orContinueWith": "Or continue with",

  // Sidebar Navigation
  "sidebar.dashboard": "Dashboard",
  "sidebar.automations": "Automations",
  "sidebar.products": "Products",
  "sidebar.businessOverview": "Business Overview",
  "sidebar.globalReports": "Global Reports",
  "sidebar.logs": "Logs",
  "sidebar.syncSettings": "Sync Settings",
  "sidebar.settings": "Settings",
  "sidebar.adminPanel": "Admin Panel",
  "sidebar.profileSettings": "Profile Settings",
  "sidebar.upgradePlan": "Upgrade Plan",
  "sidebar.logOut": "Log Out",

  // Dashboard Cards & Content
  "dashboard.automationsAnalytics": "Automations & Platform Analytics",
  "dashboard.todayPerformance": "Today's Shop Performance",
  "dashboard.products": "Products",
  "dashboard.customers": "Customers",
  "dashboard.productsLowStock": "products low on stock",
  "dashboard.sale": "Sale",
  "dashboard.purchase": "Purchase",
  "dashboard.expense": "Expense",
  "dashboard.connectOnline": "Connect with Online Business",
  "dashboard.manageOfflineShop": "Manage your offline shop — stock, sales, expenses all in one place",
  "dashboard.totalSalesToday": "Total Sales Today",
  "dashboard.onlineOffline": "Online + Offline",
  "dashboard.transactionsToday": "Transactions today",
  "dashboard.stockValue": "Stock Value",
  "dashboard.running": "Running",
  "dashboard.total": "Total",
  "dashboard.offlineSales": "Offline Sales",
  "dashboard.onlineOrders": "Online Orders",
  "dashboard.unifiedOverview": "Your unified business overview — Online & Offline connected.",
  "dashboard.onlineOverview": "Your Online Business dashboard — Automations & Analytics.",
  "dashboard.offlineShopBusiness": "Offline Shop Business",
  "dashboard.completeShopManagement": "Complete Shop Management",
  "dashboard.syncedWithOnline": "Synced with Online Business",
  "dashboard.online": "Online",
  "dashboard.offline": "Offline",

  // Sync Settings Page
  "sync.title": "Sync & Integration Settings",
  "sync.desc": "Connect your Online Business with Offline Shop for unified inventory and sales tracking",
  "sync.syncOnlineOffline": "Sync Online Business with Offline Shop",
  "sync.enableUnifiedSync": "Enable unified stock & sales sync between your online channels and physical shop",
  "sync.enabled": "Enabled",
  "sync.disabled": "Disabled",
  "sync.syncActive": "Sync is Active",
  "sync.syncActiveDesc": "Your Online and Offline inventories are now connected. Stock changes in one will reflect in the other.",
  "sync.offlineMaster": "Offline Shop = Master Inventory",
  "sync.offlineMasterDesc": "Your Offline Shop products are the source of truth. Online orders will reduce stock from here.",
  "sync.onlineToReports": "Online Sales → Unified Reports",
  "sync.onlineToReportsDesc": "Sales from Facebook, Website, etc. will appear in your Global Reports with \"Source: Online\" label.",
  "sync.whyEnable": "Why enable sync?",
  "sync.benefit1": "One inventory for both online and offline sales",
  "sync.benefit2": "Automatic stock updates when orders are placed",
  "sync.benefit3": "Unified reporting across all sales channels",
  "sync.benefit4": "Prevent overselling and stock discrepancies",
  "sync.syncCompleteWithStats": "Sync complete! {offlineToOnline} products added to Online, {onlineToOffline} products added to Offline, {stockUpdated} products stock merged.",
  "sync.productLinking": "Automatic Product Matching",
  "sync.productLinkingDesc": "Products are automatically linked by matching names. Same name = same product. Stock combines automatically!",
  "sync.howToLink": "How it works",
  "sync.linkStep1": "Enable Sync from the toggle above",
  "sync.linkStep2": "All Offline products will be added to Online inventory",
  "sync.linkStep3": "If product names match, their stock will be combined",
  "sync.linkStep4": "Sales from either side will update stock in both places",
  "sync.goToProducts": "Go to Products",
  "sync.syncTab": "Sync",

  // Offline System
  "offline.dbInitialized": "Local database initialized",
  "offline.dbError": "Failed to initialize local database",
  "offline.syncPending": "Changes pending sync",
  "offline.syncComplete": "All changes synced",
  "offline.syncFailed": "Sync failed, will retry",
  "offline.offlineMode": "You are offline",
  "offline.onlineMode": "You are online",
  "offline.lastSynced": "Last synced",
  "offline.pendingChanges": "pending changes",
  "offline.syncNow": "Sync Now",
  "offline.syncing": "Syncing...",
  "offline.syncRequired": "Sync required",
  "offline.lastOnline": "Last online",
  "offline.daysAgo": "days ago",
  "offline.hoursAgo": "hours ago",
  "offline.minutesAgo": "minutes ago",
  "offline.justNow": "Just now",
  "offline.dataSize": "Data size",
  "offline.clearData": "Clear offline data",
  "offline.clearDataConfirm": "Are you sure you want to clear all offline data? This cannot be undone.",
  "offline.dataSaved": "Data saved locally",
  "offline.subscriptionExpired": "Subscription expired",
  "offline.subscriptionExpiredDesc": "Your subscription has expired. Please go online and renew your plan to continue using the app.",
  "offline.offlineTooLong": "Offline too long",
  "offline.offlineTooLongDesc": "You have been offline for more than 7 days. Please connect to the internet to verify your subscription.",
  "offline.goOnline": "Connect to Internet",
  "offline.renewPlan": "Renew Plan",
  "offline.productsCount": "Products",
  "offline.salesCount": "Sales",
  "offline.customersCount": "Customers",
  "offline.storageUsed": "Storage used",
  "offline.savedLocally": "Saved locally",
  "offline.usingCache": "Offline Mode",
  "offline.online": "Online",
  "offline.daysRemaining": "days remaining",
  "offline.allSynced": "All synced",
  "offline.pending": "pending",
  "offline.periodExpired": "Offline Period Expired",
  "offline.periodExpiredDesc": "You've been offline for more than {days} days. Some features have been restricted.",
  "offline.unknown": "Unknown",
  "offline.restrictedFeatures": "Restricted Features",
  "offline.availableFeatures": "Available Features",
  "offline.viewReports": "View Reports",
  "offline.newPurchases": "New Purchases",
  "offline.loanManagement": "Loan Management",
  "offline.staffManagement": "Staff Management",
  "offline.viewProducts": "View Products",
  "offline.makeSales": "Make Sales",
  "offline.customerList": "Customer List",
  "offline.checking": "Checking...",
  "offline.checkConnection": "Check Connection",
  "offline.continueInLimitedMode": "Continue in Limited Mode",
};

// Bangla translations
const bn: Record<string, string> = {
  // Common
  "common.save": "সেভ করুন",
  "common.cancel": "বাতিল",
  "common.delete": "ডিলিট",
  "common.edit": "এডিট",
  "common.add": "যোগ করুন",
  "common.search": "খুঁজুন",
  "common.loading": "লোড হচ্ছে...",
  "common.refresh": "রিফ্রেশ",
  "common.export": "এক্সপোর্ট",
  "common.import": "ইম্পোর্ট",
  "common.all": "সব",
  "common.today": "আজ",
  "common.thisMonth": "এই মাস",
  "common.thisYear": "এই বছর",
  "common.paid": "পেইড",
  "common.due": "বাকি",
  "common.active": "সক্রিয়",
  "common.inactive": "নিষ্ক্রিয়",
  "common.yes": "হ্যাঁ",
  "common.no": "না",

  // Header
  "nav.features": "ফিচার",
  "nav.pricing": "মূল্য",
  "nav.faq": "জিজ্ঞাসা",
  "nav.contact": "যোগাযোগ",
  "nav.dashboard": "ড্যাশবোর্ড",
  "nav.settings": "সেটিংস",
  "nav.logout": "লগ আউট",
  "nav.login": "লগইন",
  "nav.startTrial": "ফ্রি ট্রায়াল শুরু করুন",
  "nav.theme": "থিম",
  "nav.language": "ভাষা",

  // Hero Section
  "hero.badge": "এআই-চালিত অটোমেশন",
  "hero.title1": "আপনার",
  "hero.title2": "ব্যবসা",
  "hero.title3": "সেকেন্ডে অটোমেট করুন",
  "hero.subtitle": "সম্পূর্ণ ব্যবসা অটোমেশন - ই-কমার্স ও এফ-কমার্সের জন্য এআই-চালিত প্ল্যাটফর্ম। অফলাইন দোকানের জন্য শক্তিশালী POS সিস্টেম। সব কিছু এক প্ল্যাটফর্মে।",
  "hero.subtitleBold": "২৪ ঘণ্টা ফ্রি। কোন ক্রেডিট কার্ড লাগবে না।",
  "hero.cta": "ফ্রি ট্রায়াল শুরু করুন",
  "hero.demo": "ডেমো দেখুন",
  "hero.benefit1": "২৪ ঘণ্টা ফ্রি ট্রায়াল",
  "hero.benefit2": "ক্রেডিট কার্ড লাগবে না",
  "hero.benefit3": "যেকোনো সময় বাতিল করুন",
  "hero.statsMessages": "মেসেজ",
  "hero.statsHandled": "আজ হ্যান্ডেল করা হয়েছে",
  "hero.statsSuccess": "সফল",
  "hero.statsAutoReply": "অটো-রিপ্লাই রেট",

  // Features Section
  "features.badge": "শক্তিশালী ফিচার",
  "features.title1": "আপনার দোকান",
  "features.title2": "অটোমেট করতে যা প্রয়োজন",
  "features.subtitle": "ইনস্ট্যান্ট মেসেজ রিপ্লাই থেকে ইন্টেলিজেন্ট ইমেজ রিকগনিশন — আমরা সব হ্যান্ডেল করি যাতে আপনি আপনার ব্যবসা বাড়াতে মনোযোগ দিতে পারেন।",
  "features.learnMore": "আরও জানুন",
  "features.messageReply.title": "মেসেজ অটো-রিপ্লাই",
  "features.messageReply.desc": "এআই বাংলায় কাস্টমার মেসেজে তাৎক্ষণিক উত্তর দেয়। আর কোনো সেল মিস করবেন না।",
  "features.imageRecognition.title": "ইমেজ রিকগনিশন",
  "features.imageRecognition.desc": "কাস্টমার প্রোডাক্ট ইমেজ পাঠায়, এআই স্বয়ংক্রিয়ভাবে সনাক্ত করে এবং দাম দেয়।",
  "features.voiceSupport.title": "ভয়েস সাপোর্ট",
  "features.voiceSupport.desc": "ভয়েস মেসেজ? আমরা সেকেন্ডে ট্রান্সক্রাইব করি এবং অটো উত্তর দেই।",
  "features.commentManagement.title": "কমেন্ট ম্যানেজমেন্ট",
  "features.commentManagement.desc": "স্প্যাম ডিটেকশন, খারাপ কমেন্ট অটো-ডিলিট, অপব্যবহারকারী ইউজার তাৎক্ষণিক ব্যান।",
  "features.autoInvoice.title": "অটো-ইনভয়েস",
  "features.autoInvoice.desc": "প্রতিটি অর্ডার কনফার্মেশনের পরে স্বয়ংক্রিয়ভাবে প্রফেশনাল ইনভয়েস তৈরি করুন।",
  "features.support247.title": "২৪/৭ সাপোর্ট",
  "features.support247.desc": "আপনার দোকান কখনো ঘুমায় না। দিন-রাত যেকোনো সময় কাস্টমারদের উত্তর দিন।",
  
  // Offline Shop Features
  "features.onlineBusiness": "অনলাইন বিজনেস অটোমেশন",
  "features.offlineShopBusiness": "অফলাইন শপ ম্যানেজমেন্ট",
  "features.offlineShop.title": "শপ ম্যানেজমেন্ট",
  "features.offlineShop.desc": "ইনভেন্টরি, বিক্রি এবং খরচ ট্র্যাকিং সহ সম্পূর্ণ POS সিস্টেম।",
  "features.inventory.title": "ইনভেন্টরি কন্ট্রোল",
  "features.inventory.desc": "স্টক লেভেল, মেয়াদ উত্তীর্ণ তারিখ ট্র্যাক করুন এবং লো স্টক অ্যালার্ট পান।",
  "features.customers.title": "কাস্টমার ম্যানেজমেন্ট",
  "features.customers.desc": "বাকি ট্র্যাকিং এবং কেনাকাটার ইতিহাস সহ কাস্টমার সম্পর্ক তৈরি করুন।",
  "features.reports.title": "রিপোর্ট ও অ্যানালিটিক্স",
  "features.reports.desc": "বিস্তারিত ব্যবসায়িক রিপোর্ট দিয়ে ডেটা-চালিত সিদ্ধান্ত নিন।",
  "features.expense.title": "খরচ ও ক্যাশ",
  "features.expense.desc": "খরচ ট্র্যাক করুন, ক্যাশ ফ্লো ম্যানেজ করুন এবং দৈনিক ক্যাশ ক্লোজিং।",
  "features.sync.title": "অনলাইন-অফলাইন সিংক",
  "features.sync.desc": "অনলাইন এবং অফলাইন বিক্রি চ্যানেল জুড়ে ইনভেন্টরি একীভূত করুন।",
  "features.suppliers.title": "সাপ্লায়ার ম্যানেজমেন্ট",
  "features.suppliers.desc": "সাপ্লায়ার ক্রয়, বাকি এবং পেমেন্ট ইতিহাস ট্র্যাক করুন।",
  "features.loans.title": "লোন ম্যানেজমেন্ট",
  "features.loans.desc": "কিস্তি এবং পেমেন্ট রিমাইন্ডার সহ ব্যবসায়িক লোন ট্র্যাক করুন।",

  // FAQ Section
  "faq.badge": "জিজ্ঞাসা",
  "faq.title1": "প্রায়শই জিজ্ঞাসিত",
  "faq.title2": "প্রশ্নাবলী",
  "faq.subtitle": "প্রশ্ন আছে? আমাদের কাছে উত্তর আছে। আপনি যা খুঁজছেন তা না পেলে, আমাদের সাপোর্ট টিমে যোগাযোগ করুন।",
  "faq.q1": "এটা কি সত্যিই ২৪ ঘণ্টার জন্য ফ্রি?",
  "faq.a1": "হ্যাঁ, সম্পূর্ণ ফ্রি! আপনি ২৪ ঘণ্টার জন্য সব ফিচারে সম্পূর্ণ অ্যাক্সেস পাবেন। কোন ক্রেডিট কার্ড লাগবে না। কোন লুকানো ফি নেই। পছন্দ হলে আপগ্রেড করুন। না হলে কোন সমস্যা নেই।",
  "faq.q2": "এটা কিভাবে আমার ফেসবুক শপে কানেক্ট হয়?",
  "faq.a2": "খুব সহজ! আপনার ফেসবুক অ্যাকাউন্ট দিয়ে এক-ক্লিক লগইন করুন, AutoFloy কে আপনার পেজ অ্যাক্সেস করতে অথরাইজ করুন, ব্যস হয়ে গেল। পুরো প্রক্রিয়া ৬০ সেকেন্ডেরও কম সময় নেয়।",
  "faq.q3": "এটা কি বাংলায় উত্তর দেবে?",
  "faq.a3": "অবশ্যই! আমাদের এআই বিশেষভাবে বাংলায় বুঝতে এবং স্বাভাবিকভাবে উত্তর দিতে প্রশিক্ষিত। এটি বাংলা স্ল্যাং, কথ্য ভাষা এবং এমনকি মিশ্র বাংলা-ইংরেজি (বাংলিশ) কথোপকথন নিখুঁতভাবে হ্যান্ডেল করে।",
  "faq.q4": "আমি কি এআই রেসপন্স কাস্টমাইজ করতে পারি?",
  "faq.a4": "হ্যাঁ! আপনার সম্পূর্ণ নিয়ন্ত্রণ আছে। আপনার নিজের ট্রিগার কীওয়ার্ড সেট করুন, রেসপন্স টেমপ্লেট কাস্টমাইজ করুন, টোন বাছুন (বন্ধুত্বপূর্ণ, প্রফেশনাল বা ক্যাজুয়াল), এবং এআই কে আপনার স্টাইলে রেসপন্স তৈরি করতে দিন।",
  "faq.q5": "ট্রায়াল শেষ হলে কী হয়?",
  "faq.a5": "আপনার ২৪ ঘণ্টার ট্রায়ালের পরে, আপনি আপনার প্রয়োজন অনুসারে একটি প্ল্যান বাছতে পারেন। আপনার অটোমেশন এবং সেটিংস সংরক্ষিত থাকে। আপগ্রেড না করলে, অটোমেশন পজ হয় কিন্তু কিছুই ডিলিট হয় না — আপনি যেকোনো সময় পুনরায় শুরু করতে পারেন।",
  "faq.q6": "আমার ডেটা কি নিরাপদ?",
  "faq.a6": "১০০% নিরাপদ। আমরা এন্টারপ্রাইজ-গ্রেড এনক্রিপশন ব্যবহার করি, আপনার ফেসবুক পাসওয়ার্ড কখনো সংরক্ষণ করি না, এবং শুধুমাত্র অটোমেশন চালাতে যা প্রয়োজন তাই অ্যাক্সেস করি। আপনার ডেটা আপনার — আমরা কখনো বিক্রি বা শেয়ার করি না।",
  "faq.q7": "আমি কি একাধিক ফেসবুক পেজে এটা ব্যবহার করতে পারি?",
  "faq.a7": "হ্যাঁ! আপনার প্ল্যানের উপর নির্ভর করে, আপনি ১ থেকে আনলিমিটেড ফেসবুক পেজ কানেক্ট করতে পারেন। প্রফেশনাল প্ল্যান ৫টি পেজ সাপোর্ট করে, বিজনেস প্ল্যান আনলিমিটেড পেজ অফার করে।",
  "faq.q8": "সেটআপে সাহায্য লাগলে কী হবে?",
  "faq.a8": "আমরা আপনাকে কভার করেছি! আমাদের স্টেপ-বাই-স্টেপ টিউটোরিয়াল, ভিডিও গাইড এবং ডেডিকেটেড সাপোর্ট টিম অ্যাক্সেস করুন। বিজনেস প্ল্যান ইউজাররা অগ্রাধিকার সাপোর্ট এবং কাস্টম ট্রেনিং সেশনও পান।",

  // CTA Section
  "cta.title1": "আপনার দোকান",
  "cta.title2": "অটোমেট",
  "cta.title3": "করতে প্রস্তুত?",
  "cta.subtitle": "হাজার হাজার ব্যবসায়ের সাথে যোগ দিন যারা ইতিমধ্যে এআই অটোমেশনে সময় বাঁচাচ্ছে এবং বিক্রি বাড়াচ্ছে।",
  "cta.button": "এখনই ফ্রি ট্রায়াল শুরু করুন",
  "cta.note": "ক্রেডিট কার্ড লাগবে না • যেকোনো সময় বাতিল করুন",

  // Benefits Section
  "benefits.badge": "কেন আমাদের বাছবেন",
  "benefits.title1": "তৈরি করা হয়েছে",
  "benefits.title2": "বাংলাদেশী ব্যবসার জন্য",
  "benefits.subtitle": "আমরা স্থানীয় বাজার বুঝি। তাই আমরা বাংলাদেশে ফেসবুক কমার্সের জন্য বিশেষভাবে ডিজাইন করা ফিচার তৈরি করেছি।",

  // Footer
  "footer.product": "প্রোডাক্ট",
  "footer.company": "কোম্পানি",
  "footer.resources": "রিসোর্স",
  "footer.legal": "আইনি",
  "footer.features": "ফিচার",
  "footer.pricing": "মূল্য",
  "footer.documentation": "ডকুমেন্টেশন",
  "footer.about": "আমাদের সম্পর্কে",
  "footer.blog": "ব্লগ",
  "footer.careers": "ক্যারিয়ার",
  "footer.contact": "যোগাযোগ",
  "footer.helpCenter": "হেল্প সেন্টার",
  "footer.privacyPolicy": "প্রাইভেসি পলিসি",
  "footer.termsOfService": "সেবার শর্তাবলী",
  "footer.cookiePolicy": "কুকি পলিসি",
  "footer.gdpr": "জিডিপিআর",

  // Dashboard
  "dashboard.title": "ড্যাশবোর্ড",
  "dashboard.welcome": "স্বাগতম",
  "dashboard.overview": "আপনার ব্যবসার ওভারভিউ",
  "dashboard.onlineBusiness": "অনলাইন বিজনেস",
  "dashboard.offlineShop": "অফলাইন শপ",
  "dashboard.messagesHandled": "মেসেজ হ্যান্ডেল",
  "dashboard.autoReplies": "অটো-রিপ্লাই",
  "dashboard.todayOrders": "আজকের অর্ডার",
  "dashboard.activeAutomations": "সক্রিয় অটোমেশন",
  "dashboard.todaySales": "আজকের বিক্রি",
  "dashboard.todayProfit": "আজকের লাভ",
  "dashboard.totalProducts": "মোট প্রোডাক্ট",
  "dashboard.totalCustomers": "মোট কাস্টমার",
  "dashboard.monthSales": "এই মাসের বিক্রি",
  "dashboard.monthExpenses": "এই মাসের খরচ",
  "dashboard.monthProfit": "এই মাসের লাভ",
  "dashboard.monthTarget": "মাসিক টার্গেট",
  "dashboard.salesTarget": "মাসিক বিক্রয় টার্গেট",
  "dashboard.targetAchieved": "টার্গেট অর্জিত",
  "dashboard.ofTarget": "টার্গেটের মধ্যে",
  "dashboard.inventoryValue": "স্টক মূল্য",
  "dashboard.totalDue": "মোট বাকি",
  "dashboard.lowStockAlert": "লো স্টক অ্যালার্ট",
  "dashboard.recentSales": "সাম্প্রতিক বিক্রি",
  "dashboard.recentActivity": "সাম্প্রতিক অ্যাক্টিভিটি",
  "dashboard.connectedPlatforms": "কানেক্টেড প্ল্যাটফর্ম",
  "dashboard.noPlatforms": "কোনো প্ল্যাটফর্ম কানেক্ট করা নেই",
  "dashboard.noActivity": "কোনো সাম্প্রতিক অ্যাক্টিভিটি নেই",
  "dashboard.noSales": "কোনো বিক্রি নেই",
  "dashboard.noLowStock": "কোনো লো স্টক প্রোডাক্ট নেই",
  "dashboard.successRate": "সাকসেস রেট",
  "dashboard.manageAutomations": "অটোমেশন ম্যানেজ করুন",
  "dashboard.newAutomation": "নতুন অটোমেশন",
  "dashboard.goToOfflineShop": "অফলাইন শপে যান",
  "dashboard.goToOnline": "অনলাইন বিজনেসে যান",
  "dashboard.remaining": "বাকি",
  "dashboard.returnsSummary": "রিটার্ন সারাংশ",
  "dashboard.returnsOverview": "প্রোডাক্ট রিটার্ন এবং রিফান্ড ওভারভিউ",
  "dashboard.totalReturns": "মোট রিটার্ন",
  "dashboard.totalRefunds": "মোট রিফান্ড",
  "dashboard.processed": "প্রসেসড",
  "dashboard.pending": "পেন্ডিং",
  "dashboard.topReturnReasons": "শীর্ষ রিটার্ন কারণ",
  "dashboard.returns": "রিটার্ন",
  "dashboard.noReturns": "এখনো কোনো রিটার্ন নেই",
  "dashboard.viewAll": "সব দেখুন",
  "dashboard.recentProducts": "সাম্প্রতিক প্রোডাক্ট",
  "dashboard.recentProductsDesc": "সাম্প্রতিক যোগ করা প্রোডাক্ট",
  "dashboard.noProducts": "এখনো কোনো প্রোডাক্ট যোগ হয়নি",

  // Offline Shop
  "shop.dashboard": "ড্যাশবোর্ড",
  "shop.products": "প্রোডাক্ট",
  "shop.sales": "বিক্রি",
  "shop.purchases": "ক্রয়",
  "shop.expenses": "খরচ",
  "shop.customers": "কাস্টমার",
  "shop.suppliers": "সাপ্লায়ার",
  "shop.reports": "রিপোর্ট",
  "shop.staff": "স্টাফ",
  "shop.adjustments": "অ্যাডজাস্টমেন্ট",
  "shop.cash": "ক্যাশ",
  "shop.settings": "সেটিংস",
  "shop.newProduct": "নতুন প্রোডাক্ট",
  "shop.newSale": "নতুন বিক্রি",
  "shop.newPurchase": "নতুন ক্রয়",
  "shop.newExpense": "নতুন খরচ",
  "shop.productName": "প্রোডাক্ট নাম",
  "shop.sku": "SKU",
  "shop.barcode": "বারকোড",
  "shop.purchasePrice": "ক্রয় মূল্য",
  "shop.sellingPrice": "বিক্রয় মূল্য",
  "shop.stock": "স্টক",
  "shop.category": "ক্যাটাগরি",
  "shop.unit": "ইউনিট",
  "shop.expiryDate": "মেয়াদ উত্তীর্ণের তারিখ",
  "shop.supplier": "সাপ্লায়ার",
  "shop.description": "বিবরণ",
  "shop.minStockAlert": "মিনিমাম স্টক অ্যালার্ট",
  "shop.walkInCustomer": "ওয়াক-ইন কাস্টমার",
  "shop.invoiceNumber": "ইনভয়েস নম্বর",
  "shop.customer": "কাস্টমার",
  "shop.total": "মোট",
  "shop.subtotal": "সাবটোটাল",
  "shop.discount": "ডিসকাউন্ট",
  "shop.tax": "ট্যাক্স",
  "shop.paidAmount": "পরিশোধিত",
  "shop.dueAmount": "বাকি",
  "shop.paymentMethod": "পেমেন্ট মেথড",
  "shop.paymentStatus": "পেমেন্ট স্ট্যাটাস",
  "shop.actions": "অ্যাকশন",
  
  // Shop Products Page
  "shop.productsTitle": "প্রোডাক্ট",
  "shop.productsDesc": "আপনার সব প্রোডাক্ট ম্যানেজ করুন",
  "shop.searchProducts": "প্রোডাক্ট খুঁজুন...",
  "shop.noProducts": "কোনো প্রোডাক্ট নেই",
  "shop.editProduct": "প্রোডাক্ট এডিট করুন",
  "shop.productDetails": "প্রোডাক্টের সব তথ্য দিন",
  "shop.pcs": "পিস",
  "shop.kg": "কেজি",
  "shop.liter": "লিটার",
  "shop.dozen": "ডজন",
  "shop.productAdded": "প্রোডাক্ট যোগ হয়েছে",
  "shop.productUpdated": "প্রোডাক্ট আপডেট হয়েছে",
  "shop.productDeleted": "প্রোডাক্ট ডিলিট হয়েছে",
  "shop.deleteConfirm": "আপনি কি নিশ্চিত এই প্রোডাক্ট ডিলিট করতে চান?",
  "shop.excelDownloaded": "Excel ফাইল ডাউনলোড হয়েছে",
  "shop.importSuccess": "প্রোডাক্ট ইম্পোর্ট হয়েছে",
  "shop.importFailed": "প্রোডাক্ট ইম্পোর্ট হয়নি",
  "shop.loadError": "ডেটা লোড করতে সমস্যা হয়েছে",
  "shop.errorOccurred": "সমস্যা হয়েছে, আবার চেষ্টা করুন",
  
  // Shop Sales Page
  "shop.salesTitle": "বিক্রি",
  "shop.salesDesc": "বিক্রি ম্যানেজ করুন",
  "shop.noSales": "কোনো বিক্রি নেই",
  "shop.cart": "কার্ট",
  "shop.cartEmpty": "কার্ট খালি",
  "shop.selectProduct": "প্রোডাক্ট সিলেক্ট করুন",
  "shop.selectCustomer": "কাস্টমার সিলেক্ট করুন (অপশনাল)",
  "shop.completeSale": "বিক্রি সম্পন্ন করুন",
  "shop.saleComplete": "বিক্রি সম্পন্ন!",
  "shop.invoice": "ইনভয়েস",
  "shop.date": "তারিখ",
  "shop.status": "স্ট্যাটাস",
  "shop.view": "দেখুন",
  "shop.print": "প্রিন্ট",
  "shop.close": "বন্ধ করুন",
  "shop.cashPayment": "ক্যাশ",
  "shop.bkash": "বিকাশ",
  "shop.nagad": "নগদ",
  "shop.card": "কার্ড",
  "shop.noProductsInCart": "কার্টে কোনো প্রোডাক্ট নেই",
  "shop.quantity": "পরিমাণ",
  "shop.price": "দাম",
  "shop.billTo": "বিল করা হচ্ছে",
  "shop.invoiceDetails": "ইনভয়েস বিবরণ",
  "shop.orderItems": "অর্ডার আইটেম",
  "shop.unitPrice": "একক দাম",
  
  // Shop Purchases Page
  "shop.purchasesTitle": "ক্রয়",
  "shop.purchasesDesc": "সাপ্লায়ার থেকে প্রোডাক্ট কেনার রেকর্ড",
  "shop.noPurchases": "কোনো ক্রয় নেই",
  "shop.purchaseComplete": "ক্রয় সম্পন্ন হয়েছে",
  "shop.addItems": "আইটেম যোগ করুন",
  "shop.item": "আইটেম",
  "shop.contact": "যোগাযোগ",
  "shop.notes": "নোট",
  "shop.purchaseDetails": "ক্রয়ের বিস্তারিত",
  "shop.supplierName": "সাপ্লায়ার নাম",
  "shop.buyFromSupplier": "সাপ্লায়ার থেকে প্রোডাক্ট কিনুন",
  
  // Shop Expenses Page
  "shop.expensesTitle": "খরচ",
  "shop.expensesDesc": "শপের সব খরচ ট্র্যাক করুন",
  "shop.noExpenses": "কোনো খরচ নেই",
  "shop.totalExpenses": "মোট খরচ",
  "shop.expenseAdded": "খরচ যোগ হয়েছে",
  "shop.expenseDetails": "খরচের তথ্য দিন",
  "shop.amount": "পরিমাণ",
  "shop.rent": "ভাড়া",
  "shop.electricity": "বিদ্যুৎ বিল",
  "shop.water": "পানির বিল",
  "shop.internet": "ইন্টারনেট",
  "shop.salary": "কর্মচারী বেতন",
  "shop.transport": "পরিবহন",
  "shop.marketing": "মার্কেটিং",
  "shop.maintenance": "রক্ষণাবেক্ষণ",
  "shop.officeSupply": "অফিস সাপ্লাই",
  "shop.other": "অন্যান্য",
  "shop.select": "সিলেক্ট করুন",
  "shop.bank": "ব্যাংক",
  "shop.payment": "পেমেন্ট",
  
  // Shop Customers Page
  "shop.customersTitle": "কাস্টমার",
  "shop.customersDesc": "আপনার কাস্টমারদের ম্যানেজ করুন",
  "shop.noCustomers": "কোনো কাস্টমার নেই",
  "shop.searchCustomers": "কাস্টমার খুঁজুন...",
  "shop.customerAdded": "কাস্টমার যোগ হয়েছে",
  "shop.customerUpdated": "কাস্টমার আপডেট হয়েছে",
  "shop.editCustomer": "কাস্টমার এডিট",
  "shop.newCustomer": "নতুন কাস্টমার",
  "shop.customerDetails": "কাস্টমারের তথ্য দিন",
  "shop.name": "নাম",
  "shop.phone": "ফোন",
  "shop.email": "ইমেইল",
  "shop.address": "ঠিকানা",
  "shop.totalPurchases": "মোট কেনাকাটা",
  
  // Shop Suppliers Page
  "shop.suppliersTitle": "সাপ্লায়ার",
  "shop.suppliersDesc": "আপনার সব সাপ্লায়ার ম্যানেজ করুন",
  "shop.noSuppliers": "কোনো সাপ্লায়ার নেই",
  "shop.searchSuppliers": "সাপ্লায়ার খুঁজুন...",
  "shop.newSupplier": "নতুন সাপ্লায়ার",
  "shop.editSupplier": "সাপ্লায়ার এডিট",
  "shop.supplierAdded": "সাপ্লায়ার যোগ হয়েছে",
  "shop.supplierUpdated": "সাপ্লায়ার আপডেট হয়েছে",
  "shop.supplierDeleted": "সাপ্লায়ার ডিলিট হয়েছে",
  "shop.supplierDetails": "সাপ্লায়ারের তথ্য দিন",
  "shop.details": "বিস্তারিত",
  "shop.recentPurchases": "সাম্প্রতিক ক্রয়",
  "shop.noPurchasesYet": "কোনো ক্রয় নেই",
  
  // Shop Adjustments Page
  "shop.adjustmentsTitle": "ড্যামেজ / অ্যাডজাস্টমেন্ট",
  "shop.adjustmentsDesc": "স্টক অ্যাডজাস্টমেন্ট রেকর্ড করুন",
  "shop.noAdjustments": "কোনো অ্যাডজাস্টমেন্ট নেই",
  "shop.newAdjustment": "নতুন অ্যাডজাস্টমেন্ট",
  "shop.adjustmentSaved": "অ্যাডজাস্টমেন্ট সেভ হয়েছে",
  "shop.totalLoss": "মোট লস (ড্যামেজ/এক্সপায়ারি/চুরি)",
  "shop.type": "ধরন",
  "shop.costImpact": "খরচ প্রভাব",
  "shop.reason": "কারণ",
  "shop.damage": "ড্যামেজ",
  "shop.loss": "লস",
  "shop.expired": "মেয়াদ উত্তীর্ণ",
  "shop.theft": "চুরি",
  "shop.manualIncrease": "ম্যানুয়াল বৃদ্ধি",
  "shop.manualDecrease": "ম্যানুয়াল হ্রাস",
  "shop.return": "রিটার্ন",
  "shop.allTypes": "সব ধরন",
  "shop.stockAdjustment": "নতুন স্টক অ্যাডজাস্টমেন্ট",
  "shop.adjustmentDetails": "স্টক পরিবর্তনের তথ্য দিন",
  "shop.selectProductAdj": "প্রোডাক্ট সিলেক্ট করুন",
  
  // Shop Cash Page
  "shop.cashTitle": "ক্যাশ ও পেমেন্ট",
  "shop.cashDesc": "আপনার ক্যাশবুক ম্যানেজ করুন",
  "shop.noTransactions": "কোনো ট্রানজাকশন নেই",
  "shop.newTransaction": "নতুন ট্রানজাকশন",
  "shop.transactionSaved": "ট্রানজাকশন সেভ হয়েছে",
  "shop.currentBalance": "বর্তমান ব্যালেন্স",
  "shop.totalDeposit": "মোট জমা",
  "shop.totalWithdraw": "মোট খরচ",
  "shop.customerDue": "কাস্টমার বাকি",
  "shop.supplierDue": "সাপ্লায়ার বাকি",
  "shop.noDue": "কোনো বাকি নেই",
  "shop.deposit": "জমা",
  "shop.withdraw": "খরচ",
  "shop.source": "সোর্স",
  "shop.ownerDeposit": "মালিক জমা",
  "shop.ownerWithdraw": "মালিক উত্তোলন",
  "shop.dueCollection": "বাকি আদায়",
  "shop.duePayment": "বাকি পরিশোধ",
  "shop.adjustment": "অ্যাডজাস্টমেন্ট",
  "shop.sale": "বিক্রি",
  "shop.purchase": "ক্রয়",
  "shop.expense": "খরচ",
  "shop.cashIn": "জমা (Cash In)",
  "shop.cashOut": "খরচ (Cash Out)",
  "shop.selectCustomerDue": "কাস্টমার সিলেক্ট করুন",
  "shop.selectSupplierDue": "সাপ্লায়ার সিলেক্ট করুন",
  "shop.transactionDetails": "ক্যাশ জমা বা খরচের তথ্য দিন",
  
  // Shop Staff Page
  "shop.staffTitle": "স্টাফ ও পারমিশন",
  "shop.staffDesc": "আপনার দোকানের স্টাফ ম্যানেজ করুন",
  "shop.noStaff": "কোনো স্টাফ নেই",
  "shop.newStaff": "নতুন স্টাফ",
  "shop.editStaff": "স্টাফ এডিট",
  "shop.staffAdded": "স্টাফ যোগ হয়েছে",
  "shop.staffUpdated": "স্টাফ আপডেট হয়েছে",
  "shop.staffDeleted": "স্টাফ ডিলিট হয়েছে",
  "shop.staffDetails": "স্টাফের তথ্য এবং পারমিশন সেট করুন",
  "shop.role": "রোল",
  "shop.permissions": "পারমিশন",
  "shop.owner": "মালিক",
  "shop.manager": "ম্যানেজার",
  "shop.cashier": "ক্যাশিয়ার",
  "shop.staffMember": "স্টাফ",
  "shop.canSeeReports": "রিপোর্ট দেখতে পারবে",
  "shop.canEditProducts": "প্রোডাক্ট এডিট করতে পারবে",
  "shop.canRecordPurchases": "ক্রয় রেকর্ড করতে পারবে",
  "shop.canRecordSales": "বিক্রি রেকর্ড করতে পারবে",
  "shop.canSeeProfit": "লাভ দেখতে পারবে",
  "shop.canManageStaff": "স্টাফ ম্যানেজ করতে পারবে",
  "shop.staffAccessControl": "স্টাফ অ্যাক্সেস কন্ট্রোল",
  "shop.staffAccessDesc": "এখানে আপনি আপনার দোকানের জন্য স্টাফ তৈরি করতে পারেন এবং তাদের কি কি করার পারমিশন আছে তা নির্ধারণ করতে পারেন। ভবিষ্যতে প্রতিটি স্টাফ আলাদা আলাদা লগইন করতে পারবে।",
  "shop.emailPhone": "ইমেইল/ফোন",
  
  // Shop Reports Page
  "shop.reportsTitle": "রিপোর্ট",
  "shop.reportsDesc": "বিস্তারিত রিপোর্ট দেখুন এবং ডাউনলোড করুন",
  "shop.selectDate": "তারিখ সিলেক্ট করুন",
  "shop.start": "শুরু",
  "shop.end": "শেষ",
  "shop.salesReport": "বিক্রি রিপোর্ট",
  "shop.salesReportDesc": "নির্বাচিত সময়ের বিক্রির বিস্তারিত",
  "shop.expensesReport": "খরচ রিপোর্ট",
  "shop.expensesReportDesc": "নির্বাচিত সময়ের খরচের বিস্তারিত",
  "shop.inventoryReport": "ইনভেন্টরি রিপোর্ট",
  "shop.inventoryReportDesc": "বর্তমান স্টকের বিস্তারিত",
  "shop.download": "ডাউনলোড",
  "shop.reportDownloaded": "রিপোর্ট ডাউনলোড হয়েছে",
  "shop.clickToViewReport": "রিপোর্ট দেখতে উপরে ক্লিক করুন",
  "shop.totalSales": "মোট বিক্রি",
  "shop.totalProfit": "মোট লাভ",
  "shop.totalOrders": "মোট অর্ডার",
  "shop.stockValue": "স্টক মূল্য",
  "shop.lowStock": "লো স্টক",
  
  // Shop Settings Page
  "shop.settingsTitle": "শপ সেটিংস",
  "shop.settingsDesc": "আপনার শপের তথ্য কনফিগার করুন",
  "shop.shopInfo": "শপ তথ্য",
  "shop.shopInfoDesc": "এই তথ্য ইনভয়েসে দেখানো হবে",
  "shop.shopName": "শপের নাম",
  "shop.shopNamePlaceholder": "আপনার শপের নাম",
  "shop.shopAddress": "ঠিকানা",
  "shop.shopAddressPlaceholder": "শপের ঠিকানা",
  "shop.shopPhone": "ফোন",
  "shop.shopEmail": "ইমেইল",
  "shop.invoiceSettings": "ইনভয়েস সেটিংস",
  "shop.invoiceSettingsDesc": "ইনভয়েস কাস্টমাইজ করুন",
  "shop.invoicePrefix": "ইনভয়েস প্রিফিক্স",
  "shop.taxRate": "ট্যাক্স রেট (%)",
  "shop.invoiceFooter": "ইনভয়েস ফুটার",
  "shop.invoiceFooterPlaceholder": "ধন্যবাদ! আবার আসবেন।",
  "shop.termsAndConditions": "শর্তাবলী",
  "shop.termsAndConditionsPlaceholder": "ইনভয়েসে প্রদর্শিত হবে এমন শর্তাবলী লিখুন...",
  "shop.invoiceFormat": "ইনভয়েস ফরম্যাট",
  "shop.simpleInvoice": "সিম্পল ইনভয়েস",
  "shop.simpleInvoiceDesc": "বেসিক তথ্য সহ পরিষ্কার মিনিমাল ডিজাইন",
  "shop.betterInvoice": "বেটার ইনভয়েস",
  "shop.betterInvoiceDesc": "ডার্ক হেডার ও অরেঞ্জ অ্যাকসেন্ট সহ প্রফেশনাল ডিজাইন",
  "shop.settingsSaved": "সেটিংস সেভ হয়েছে",
  "shop.saving": "সেভ হচ্ছে...",
  "shop.shopLogo": "শপ লোগো",
  "shop.uploadLogo": "লোগো আপলোড করুন",
  "shop.uploading": "আপলোড হচ্ছে...",
  "shop.logoUploaded": "লোগো সফলভাবে আপলোড হয়েছে",
  "shop.logoRemoved": "লোগো মুছে ফেলা হয়েছে",
  "shop.logoHint": "PNG, JPG সর্বোচ্চ 2MB। এটি ইনভয়েসে দেখানো হবে।",
  "shop.invalidImageType": "অনুগ্রহ করে একটি ইমেজ ফাইল নির্বাচন করুন",
  "shop.imageTooLarge": "ইমেজ 2MB এর কম হতে হবে",
  
  // Shop Layout
  "shop.offlineShop": "অফলাইন শপ",
  "shop.youAreIn": "আপনি এখানে আছেন:",
  "shop.offlineShopBusiness": "অফলাইন শপ বিজনেস",
  "shop.goToOnline": "অনলাইন বিজনেসে যান",
  "shop.switchSection": "সেকশন সুইচ করুন",
  "shop.overview": "ওভারভিউ",
  "shop.productsStock": "প্রোডাক্ট ও স্টক",
  "shop.purchasesBuyImport": "ক্রয় (কিনুন/ইম্পোর্ট)",
  "shop.salesSellPOS": "বিক্রি (সেল/POS)",
  "shop.returns": "সকল রিটার্ন",
  "shop.damageAdjustments": "ড্যামেজ/অ্যাডজাস্টমেন্ট",
  "shop.cashPayments": "ক্যাশ ও পেমেন্ট",
  "shop.staffPermissions": "স্টাফ ও পারমিশন",
  "shop.settingsSync": "সেটিংস ও সিঙ্ক",
  "shop.update": "আপডেট",
  
  // Additional Shop Translations
  "shop.noStock": "স্টকে এত পণ্য নেই",
  "shop.selectAtLeastOne": "কমপক্ষে একটি প্রোডাক্ট সিলেক্ট করুন",
  "shop.returnComplete": "রিটার্ন সম্পন্ন!",
  "shop.selectProductComplete": "প্রোডাক্ট সিলেক্ট করুন এবং বিক্রি সম্পন্ন করুন",
  "shop.customerNameOptional": "কাস্টমার নাম (ঐচ্ছিক)",
  "shop.phoneOptional": "ফোন নম্বর (ঐচ্ছিক)",
  "shop.thankYou": "ধন্যবাদ! আবার আসবেন।",
  "shop.productReturn": "প্রোডাক্ট রিটার্ন",
  "shop.selectReturnItems": "রিটার্ন আইটেম সিলেক্ট করুন",
  "shop.max": "সর্বোচ্চ",
  "shop.returnReason": "রিটার্নের কারণ",
  "shop.selectReason": "কারণ সিলেক্ট করুন",
  "shop.defective": "ত্রুটিপূর্ণ প্রোডাক্ট",
  "shop.wrongItem": "ভুল প্রোডাক্ট",
  "shop.notSatisfied": "সন্তুষ্ট নয়",
  "shop.sizeIssue": "সাইজ সমস্যা",
  "shop.notesOptional": "অতিরিক্ত নোট (ঐচ্ছিক)",
  "shop.totalRefund": "মোট রিফান্ড",
  "shop.confirmReturn": "রিটার্ন নিশ্চিত করুন",
  "shop.reasonPlaceholder": "যেমন: ভেঙে গেছে, পানি লেগেছে...",

  // Settings
  "settings.title": "সেটিংস",
  "settings.account": "অ্যাকাউন্ট",
  "settings.automation": "অটোমেশন",
  "settings.automations": "অটোমেশন",
  "settings.connections": "কানেকশন",
  "settings.services": "সার্ভিস",
  "settings.notifications": "নোটিফিকেশন",
  "settings.billing": "বিলিং",
  "settings.language": "ভাষা",
  "settings.languageDesc": "আপনার পছন্দের ইন্টারফেস ভাষা বাছুন",
  "settings.english": "English",
  "settings.bengali": "বাংলা (Bengali)",
  "settings.profile": "প্রোফাইল তথ্য",
  "settings.profileDesc": "আপনার ব্যক্তিগত তথ্য আপডেট করুন",
  "settings.name": "পুরো নাম",
  "settings.email": "ইমেইল ঠিকানা",
  "settings.phone": "ফোন নম্বর",
  "settings.password": "পাসওয়ার্ড",
  "settings.changePassword": "পাসওয়ার্ড পরিবর্তন",
  "settings.deleteAccount": "অ্যাকাউন্ট ডিলিট",
  "settings.deleteAccountDesc": "আপনার অ্যাকাউন্ট এবং সব ডেটা স্থায়ীভাবে মুছে ফেলুন",
  "settings.saveChanges": "পরিবর্তন সংরক্ষণ করুন",
  "settings.aiPreferences": "এআই প্রেফারেন্স",
  "settings.responseTone": "রেসপন্স টোন",
  "settings.responseLanguage": "রেসপন্স ভাষা",
  "settings.friendly": "বন্ধুত্বপূর্ণ",
  "settings.professional": "প্রফেশনাল",
  "settings.casual": "ক্যাজুয়াল",
  "settings.emailNotifications": "ইমেইল নোটিফিকেশন",
  "settings.pushNotifications": "পুশ নোটিফিকেশন",
  "settings.soundAlerts": "সাউন্ড অ্যালার্ট",
  "settings.dailyDigest": "ডেইলি ডাইজেস্ট",
  "settings.syncSettings": "সিঙ্ক সেটিংস",
  "settings.syncDesc": "আপনার অনলাইন এবং অফলাইন বিজনেস ডেটা কানেক্ট করুন",
  "settings.enableSync": "সিঙ্ক চালু করুন",
  "settings.syncEnabled": "সিঙ্ক চালু আছে",
  "settings.syncDisabled": "সিঙ্ক বন্ধ আছে",
  "settings.currentPlan": "বর্তমান প্ল্যান",
  "settings.upgradePlan": "প্ল্যান আপগ্রেড করুন",
  "settings.manageBilling": "বিলিং ম্যানেজ করুন",
  "settings.dangerZone": "বিপদ এলাকা",
  "settings.dangerZoneDesc": "আপনার অ্যাকাউন্টকে প্রভাবিত করে এমন অপরিবর্তনীয় কার্যক্রম",
  "settings.deleteAccountConfirm": "এটি আপনার অ্যাকাউন্ট এবং সমস্ত সংশ্লিষ্ট ডেটা স্থায়ীভাবে মুছে ফেলবে।",

  // Auth
  "auth.login": "লগইন",
  "auth.signup": "সাইন আপ",
  "auth.forgotPassword": "পাসওয়ার্ড ভুলে গেছেন?",
  "auth.resetPassword": "পাসওয়ার্ড রিসেট করুন",
  "auth.email": "ইমেইল",
  "auth.password": "পাসওয়ার্ড",
  "auth.confirmPassword": "পাসওয়ার্ড নিশ্চিত করুন",
  "auth.rememberMe": "আমাকে মনে রাখুন",
  "auth.noAccount": "অ্যাকাউন্ট নেই?",
  "auth.hasAccount": "অ্যাকাউন্ট আছে?",
  "auth.createAccount": "অ্যাকাউন্ট তৈরি করুন",
  "auth.loginWithFacebook": "ফেসবুক দিয়ে লগইন",
  "auth.orContinueWith": "অথবা লগইন করুন",

  // Sidebar Navigation
  "sidebar.dashboard": "ড্যাশবোর্ড",
  "sidebar.automations": "অটোমেশন",
  "sidebar.products": "প্রোডাক্ট",
  "sidebar.businessOverview": "বিজনেস ওভারভিউ",
  "sidebar.globalReports": "গ্লোবাল রিপোর্ট",
  "sidebar.logs": "লগ",
  "sidebar.syncSettings": "সিঙ্ক সেটিংস",
  "sidebar.settings": "সেটিংস",
  "sidebar.adminPanel": "অ্যাডমিন প্যানেল",
  "sidebar.profileSettings": "প্রোফাইল সেটিংস",
  "sidebar.upgradePlan": "প্ল্যান আপগ্রেড",
  "sidebar.logOut": "লগ আউট",

  // Dashboard Cards & Content
  "dashboard.automationsAnalytics": "অটোমেশন ও প্ল্যাটফর্ম অ্যানালিটিক্স",
  "dashboard.todayPerformance": "আজকের শপ পারফরম্যান্স",
  "dashboard.products": "প্রোডাক্ট",
  "dashboard.customers": "কাস্টমার",
  "dashboard.productsLowStock": "প্রোডাক্ট লো স্টকে আছে",
  "dashboard.sale": "বিক্রি",
  "dashboard.purchase": "ক্রয়",
  "dashboard.expense": "খরচ",
  "dashboard.connectOnline": "অনলাইন বিজনেসের সাথে কানেক্ট করুন",
  "dashboard.manageOfflineShop": "আপনার অফলাইন দোকান ম্যানেজ করুন — স্টক, বিক্রয়, খরচ সব এক জায়গায়",
  "dashboard.totalSalesToday": "আজকের মোট বিক্রি",
  "dashboard.onlineOffline": "অনলাইন + অফলাইন",
  "dashboard.transactionsToday": "আজকের ট্রানজেকশন",
  "dashboard.stockValue": "স্টক মূল্য",
  "dashboard.running": "চলমান",
  "dashboard.total": "মোট",
  "dashboard.offlineSales": "অফলাইন বিক্রি",
  "dashboard.onlineOrders": "অনলাইন অর্ডার",
  "dashboard.unifiedOverview": "আপনার সমন্বিত ব্যবসার ওভারভিউ — অনলাইন ও অফলাইন কানেক্টেড।",
  "dashboard.onlineOverview": "আপনার অনলাইন বিজনেস ড্যাশবোর্ড — অটোমেশন ও অ্যানালিটিক্স।",
  "dashboard.offlineShopBusiness": "অফলাইন শপ বিজনেস",
  "dashboard.completeShopManagement": "সম্পূর্ণ দোকান ম্যানেজমেন্ট",
  "dashboard.syncedWithOnline": "অনলাইন বিজনেসের সাথে সিঙ্ক্রোনাইজড",
  "dashboard.online": "অনলাইন",
  "dashboard.offline": "অফলাইন",

  // Sync Settings Page
  "sync.title": "সিঙ্ক ও ইন্টিগ্রেশন সেটিংস",
  "sync.desc": "সমন্বিত ইনভেন্টরি এবং সেলস ট্র্যাকিংয়ের জন্য আপনার অনলাইন বিজনেসকে অফলাইন শপের সাথে কানেক্ট করুন",
  "sync.syncOnlineOffline": "অনলাইন বিজনেস অফলাইন শপের সাথে সিঙ্ক করুন",
  "sync.enableUnifiedSync": "আপনার অনলাইন চ্যানেল এবং ফিজিক্যাল শপের মধ্যে সমন্বিত স্টক ও সেলস সিঙ্ক চালু করুন",
  "sync.enabled": "চালু",
  "sync.disabled": "বন্ধ",
  "sync.syncActive": "সিঙ্ক সক্রিয়",
  "sync.syncActiveDesc": "আপনার অনলাইন এবং অফলাইন ইনভেন্টরি এখন কানেক্টেড। একটিতে স্টক পরিবর্তন অন্যটিতে প্রতিফলিত হবে।",
  "sync.offlineMaster": "অফলাইন শপ = মাস্টার ইনভেন্টরি",
  "sync.offlineMasterDesc": "আপনার অফলাইন শপ প্রোডাক্টগুলো সত্যের উৎস। অনলাইন অর্ডার এখান থেকে স্টক কমাবে।",
  "sync.onlineToReports": "অনলাইন সেলস → সমন্বিত রিপোর্ট",
  "sync.onlineToReportsDesc": "ফেসবুক, ওয়েবসাইট ইত্যাদি থেকে সেলস আপনার গ্লোবাল রিপোর্টে \"সোর্স: অনলাইন\" লেবেলসহ দেখাবে।",
  "sync.whyEnable": "সিঙ্ক কেন চালু করবেন?",
  "sync.benefit1": "অনলাইন এবং অফলাইন সেলস উভয়ের জন্য একটি ইনভেন্টরি",
  "sync.benefit2": "অর্ডার প্লেস হলে স্বয়ংক্রিয় স্টক আপডেট",
  "sync.benefit3": "সব সেলস চ্যানেল জুড়ে সমন্বিত রিপোর্টিং",
  "sync.benefit4": "ওভারসেলিং এবং স্টক অসামঞ্জস্য প্রতিরোধ করুন",
  "sync.syncCompleteWithStats": "সিঙ্ক সম্পন্ন! {offlineToOnline}টি প্রোডাক্ট অনলাইনে যোগ হয়েছে, {onlineToOffline}টি প্রোডাক্ট অফলাইনে যোগ হয়েছে, {stockUpdated}টি প্রোডাক্টের স্টক মার্জ হয়েছে।",
  "sync.productLinking": "অটোমেটিক প্রোডাক্ট ম্যাচিং",
  "sync.productLinkingDesc": "প্রোডাক্টগুলো নাম অনুযায়ী স্বয়ংক্রিয়ভাবে লিঙ্ক হয়। একই নাম = একই প্রোডাক্ট। স্টক অটোমেটিক যোগ হয়!",
  "sync.howToLink": "কিভাবে কাজ করে",
  "sync.linkStep1": "উপরের টগল থেকে Sync চালু করুন",
  "sync.linkStep2": "সব অফলাইন প্রোডাক্ট অনলাইন ইনভেন্টরিতে যোগ হবে",
  "sync.linkStep3": "যদি প্রোডাক্টের নাম মিলে যায়, তাদের স্টক একসাথে হবে",
  "sync.linkStep4": "যেকোনো পাশ থেকে বিক্রি হলে দুই জায়গায় স্টক আপডেট হবে",
  "sync.goToProducts": "প্রোডাক্টে যান",
  "sync.syncTab": "সিঙ্ক",

  // Offline System
  "offline.dbInitialized": "লোকাল ডেটাবেস প্রস্তুত",
  "offline.dbError": "লোকাল ডেটাবেস শুরু করা যায়নি",
  "offline.syncPending": "পরিবর্তন সিঙ্ক হওয়ার অপেক্ষায়",
  "offline.syncComplete": "সব পরিবর্তন সিঙ্ক হয়েছে",
  "offline.syncFailed": "সিঙ্ক ব্যর্থ, আবার চেষ্টা করা হবে",
  "offline.offlineMode": "আপনি অফলাইনে আছেন",
  "offline.onlineMode": "আপনি অনলাইনে আছেন",
  "offline.lastSynced": "সর্বশেষ সিঙ্ক",
  "offline.pendingChanges": "টি পরিবর্তন অপেক্ষায়",
  "offline.syncNow": "এখন সিঙ্ক করুন",
  "offline.syncing": "সিঙ্ক হচ্ছে...",
  "offline.syncRequired": "সিঙ্ক প্রয়োজন",
  "offline.lastOnline": "সর্বশেষ অনলাইন",
  "offline.daysAgo": "দিন আগে",
  "offline.hoursAgo": "ঘন্টা আগে",
  "offline.minutesAgo": "মিনিট আগে",
  "offline.justNow": "এইমাত্র",
  "offline.dataSize": "ডেটা সাইজ",
  "offline.clearData": "অফলাইন ডেটা মুছুন",
  "offline.clearDataConfirm": "আপনি কি নিশ্চিত যে সব অফলাইন ডেটা মুছে ফেলতে চান? এটি পূর্বাবস্থায় ফেরানো যাবে না।",
  "offline.dataSaved": "ডেটা লোকালে সেভ হয়েছে",
  "offline.subscriptionExpired": "সাবস্ক্রিপশন শেষ হয়ে গেছে",
  "offline.subscriptionExpiredDesc": "আপনার সাবস্ক্রিপশন শেষ হয়ে গেছে। অ্যাপ ব্যবহার চালিয়ে যেতে অনলাইনে যান এবং প্ল্যান রিনিউ করুন।",
  "offline.offlineTooLong": "অনেক দিন অফলাইন",
  "offline.offlineTooLongDesc": "আপনি ৭ দিনের বেশি অফলাইন আছেন। সাবস্ক্রিপশন যাচাই করতে ইন্টারনেটে সংযুক্ত হন।",
  "offline.goOnline": "ইন্টারনেটে সংযুক্ত হন",
  "offline.renewPlan": "প্ল্যান রিনিউ করুন",
  "offline.productsCount": "প্রোডাক্ট",
  "offline.salesCount": "বিক্রয়",
  "offline.customersCount": "কাস্টমার",
  "offline.storageUsed": "স্টোরেজ ব্যবহৃত",
  "offline.savedLocally": "লোকালে সেভ হয়েছে",
  "offline.usingCache": "অফলাইন মোড",
  "offline.online": "অনলাইন",
  "offline.daysRemaining": "দিন বাকি",
  "offline.allSynced": "সব আপডেট হয়েছে",
  "offline.pending": "পেন্ডিং",
  "offline.periodExpired": "অফলাইন সময়সীমা শেষ",
  "offline.periodExpiredDesc": "আপনি {days} দিনের বেশি সময় ধরে অফলাইন আছেন। কিছু ফিচার সীমাবদ্ধ করা হয়েছে।",
  "offline.unknown": "অজানা",
  "offline.restrictedFeatures": "সীমাবদ্ধ ফিচার",
  "offline.availableFeatures": "ব্যবহারযোগ্য ফিচার",
  "offline.viewReports": "রিপোর্ট দেখা",
  "offline.newPurchases": "নতুন ক্রয়",
  "offline.loanManagement": "লোন ম্যানেজমেন্ট",
  "offline.staffManagement": "স্টাফ ম্যানেজমেন্ট",
  "offline.viewProducts": "পণ্য দেখুন",
  "offline.makeSales": "বিক্রয় করুন",
  "offline.customerList": "কাস্টমার তালিকা",
  "offline.checking": "চেক করা হচ্ছে...",
  "offline.checkConnection": "কানেকশন চেক করুন",
  "offline.continueInLimitedMode": "সীমিত মোডে চালিয়ে যান",
};

const translations: Record<Language, Record<string, string>> = { en, bn };

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved as Language) || "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
